'use client';

/**
 * SearchAiz - Main Component
 *
 * The primary entry point for the SearchAiz unified search system.
 * Automatically adapts UI based on mode and context.
 *
 * Usage:
 * ```tsx
 * // Header search (command palette)
 * <SearchAiz context="header" />
 *
 * // Full page search
 * <SearchAiz mode="full" context="fullPage" />
 *
 * // Embedded simple search
 * <SearchAiz mode="simple" context="embedded" />
 * ```
 */

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Search, Command } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { SearchAizProvider } from './providers/SearchAizProvider';
import { useSearchAizSettings } from './hooks/useSearchAizSettings';
import { InlineSearch } from './core/InlineSearch';
import type {
  SearchAizProps,
  SearchMode,
  SearchContext,
  SearchResult,
  PartialSettings,
} from './types';

// =============================================================================
// SEARCH TRIGGER BUTTON
// =============================================================================

interface SearchTriggerProps {
  onClick: () => void;
  placeholder?: string;
  className?: string;
}

function SearchTrigger({ onClick, placeholder = 'Search...', className }: SearchTriggerProps) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-4 h-9 px-3 py-2',
        'bg-slate-900/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800/50',
        'text-slate-400 hover:text-slate-300',
        'min-w-[200px] w-full max-w-sm',
        className
      )}
    >
      <span className="flex items-center gap-2">
        <Search className="w-4 h-4" />
        <span className="text-sm font-normal">{placeholder}</span>
      </span>
      <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-mono bg-slate-800 border border-slate-700 rounded">
        <Command className="w-3 h-3" />
        <span>K</span>
      </kbd>
    </Button>
  );
}

// =============================================================================
// HEADER SEARCH MODE - Inline search with dropdown
// =============================================================================

interface HeaderSearchProps {
  placeholder?: string;
  initialQuery?: string;
  onSelect?: (result: SearchResult) => void;
  className?: string;
}

function HeaderSearch({ placeholder, initialQuery, onSelect, className }: HeaderSearchProps) {
  return (
    <InlineSearch
      placeholder={placeholder}
      initialQuery={initialQuery}
      onSelect={onSelect}
      className={className}
    />
  );
}

// =============================================================================
// SIMPLE SEARCH MODE (placeholder for future)
// =============================================================================

interface SimpleSearchProps {
  placeholder?: string;
  initialQuery?: string;
  onSelect?: (result: SearchResult) => void;
  className?: string;
}

function SimpleSearch({ placeholder, initialQuery, onSelect, className }: SimpleSearchProps) {
  // For now, just use the header search style
  // In Release 2, this will have its own inline UI
  return (
    <HeaderSearch
      placeholder={placeholder}
      initialQuery={initialQuery}
      onSelect={onSelect}
      className={className}
    />
  );
}

// =============================================================================
// AI ASSISTED SEARCH MODE (placeholder for Release 2)
// =============================================================================

interface AiAssistedSearchProps {
  placeholder?: string;
  initialQuery?: string;
  onSelect?: (result: SearchResult) => void;
  className?: string;
}

function AiAssistedSearch({ placeholder, initialQuery, onSelect, className }: AiAssistedSearchProps) {
  // Placeholder - will be expanded in Release 2
  return (
    <SimpleSearch
      placeholder={placeholder}
      initialQuery={initialQuery}
      onSelect={onSelect}
      className={className}
    />
  );
}

// =============================================================================
// FULL SEARCH MODE (placeholder for Release 2)
// =============================================================================

interface FullSearchProps {
  placeholder?: string;
  initialQuery?: string;
  onSelect?: (result: SearchResult) => void;
  className?: string;
}

function FullSearch({ placeholder, initialQuery, onSelect, className }: FullSearchProps) {
  // Placeholder - will be expanded in Release 2
  return (
    <SimpleSearch
      placeholder={placeholder}
      initialQuery={initialQuery}
      onSelect={onSelect}
      className={className}
    />
  );
}

// =============================================================================
// CONVERSATIONAL SEARCH MODE (placeholder for Release 3)
// =============================================================================

interface ConversationalSearchProps {
  placeholder?: string;
  initialQuery?: string;
  onSelect?: (result: SearchResult) => void;
  className?: string;
}

function ConversationalSearch({ placeholder, initialQuery, onSelect, className }: ConversationalSearchProps) {
  // Placeholder - will be expanded in Release 3
  return (
    <SimpleSearch
      placeholder={placeholder}
      initialQuery={initialQuery}
      onSelect={onSelect}
      className={className}
    />
  );
}

// =============================================================================
// MAIN SEARCHAIZ COMPONENT
// =============================================================================

/**
 * Internal component that renders based on resolved mode
 */
function SearchAizInternal({
  mode = 'auto',
  context = 'header',
  placeholder = 'Search...',
  initialQuery,
  onResultSelect,
  className,
}: SearchAizProps) {
  const { settings } = useSearchAizSettings();

  // Resolve mode from settings if 'auto'
  const resolvedMode: SearchMode = useMemo(() => {
    if (mode !== 'auto') return mode;

    // Get context-specific mode override if available
    const contextOverride = settings.contextOverrides?.[context];
    if (contextOverride?.mode?.default) {
      return contextOverride.mode.default;
    }

    return settings.mode.default;
  }, [mode, context, settings]);

  // Render appropriate mode
  const modeProps = {
    placeholder,
    initialQuery,
    onSelect: onResultSelect,
    className,
  };

  switch (resolvedMode) {
    case 'simple':
      return <SimpleSearch {...modeProps} />;
    case 'ai-assisted':
      return <AiAssistedSearch {...modeProps} />;
    case 'conversational':
      return <ConversationalSearch {...modeProps} />;
    case 'full':
      return <FullSearch {...modeProps} />;
    default:
      return <SimpleSearch {...modeProps} />;
  }
}

// =============================================================================
// EXPORTED SEARCHAIZ WITH PROVIDER
// =============================================================================

export interface SearchAizWithProviderProps extends SearchAizProps {
  /** Portal-level settings */
  portalSettings?: PartialSettings;
  /** Developer-level settings */
  developerSettings?: PartialSettings;
  /** User's permissions */
  userPermissions?: string[];
  /** User's billing tier */
  billingTier?: 'free' | 'starter' | 'professional' | 'enterprise';
}

/**
 * SearchAiz component with built-in provider
 * Use this when SearchAiz is the only search component on the page
 */
export function SearchAizWithProvider({
  portalSettings,
  developerSettings,
  userPermissions,
  billingTier,
  context = 'header',
  ...props
}: SearchAizWithProviderProps) {
  return (
    <SearchAizProvider
      portalSettings={portalSettings}
      developerSettings={developerSettings}
      userPermissions={userPermissions}
      billingTier={billingTier}
      context={context}
    >
      <SearchAizInternal context={context} {...props} />
    </SearchAizProvider>
  );
}

/**
 * SearchAiz component (requires SearchAizProvider ancestor)
 * Use this when you have multiple search components sharing a provider
 */
export function SearchAiz(props: SearchAizProps) {
  return <SearchAizInternal {...props} />;
}

// Default export is the standalone version
export default SearchAizWithProvider;
